import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { getUsers, createUser, deleteUser } from '../../store/userSlice';
import EditUserModal from './EditUserModal';

const UserManagement = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [role, setRole] = useState('Staff');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [userPasswords, setUserPasswords] = useState({});

  const dispatch = useDispatch();
  const { users, loading, error } = useSelector((state) => state.users);
  const { userInfo } = useSelector((state) => state.auth);

  // Function to fetch users
  const fetchUsers = () => {
    dispatch(getUsers());
  };

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const submitHandler = (e) => {
    e.preventDefault();
    dispatch(createUser({ name, email, mobileNumber, dateOfBirth, role })).then(() => {
        toast.success('User created successfully!');
        setName('');
        setEmail('');
        setMobileNumber('');
        setDateOfBirth('');
        setRole('Staff');
    });
  };

  const deleteHandler = (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      dispatch(deleteUser(id)).then(() => {
        toast.success('User deleted successfully!');
      });
    }
  };

  const openModal = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedUser(null);
    setIsModalOpen(false);
  };

  const resetPassword = async (userId, userName, userEmail) => {
    if (window.confirm(`Are you sure you want to reset password for ${userName}?\n\nThis will:\n• Generate a new random password\n• Send it to ${userEmail}\n• Force user to change password on next login`)) {
      try {
        const response = await fetch(`/api/users/${userId}/reset-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userInfo.token}`,
          },
        });
        
        const data = await response.json();
        
        if (response.ok) {
          toast.success(`Password reset successfully! New password sent to ${userEmail}`);
          // Refresh users list
          fetchUsers();
        } else {
          toast.error(data.message || 'Failed to reset password');
        }
      } catch (error) {
        console.error('Reset password error:', error);
        toast.error('Failed to reset password. Please try again.');
      }
    }
  };

  const hidePassword = (userId) => {
    setVisiblePasswords(prev => ({ ...prev, [userId]: false }));
  };

  return (
    <div>
      {/* Add User Form */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-2xl font-bold mb-4">Add New User</h2>
        <form onSubmit={submitHandler} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
            required
          />
          <input
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
            required
          />
          <input
            type="text"
            placeholder="Mobile Number"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
            required
          />
          <input
            type="date"
            placeholder="Date of Birth"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg"
            title="Date of Birth (Optional)"
          />
          <select 
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg" 
            required
          >
            <option value="Staff">Staff</option>
            <option value="Admin">Admin</option>
          </select>
          <button
            type="submit"
            className="md:col-span-2 w-full py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add User'}
          </button>
        </form>
      </div>

      {/* Users List */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-4">Existing Users</h2>
        <div className="overflow-x-auto">
          {loading ? <p>Loading users...</p> : (
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">Name</th>
                  <th className="py-2 px-4 border-b">Email</th>
                  <th className="py-2 px-4 border-b">Mobile</th>
                  <th className="py-2 px-4 border-b">Date of Birth</th>
                  <th className="py-2 px-4 border-b">Role</th>
                  <th className="py-2 px-4 border-b">Password</th>
                  <th className="py-2 px-4 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user._id}>
                    <td className="py-2 px-4 border-b text-center">{user.name}</td>
                    <td className="py-2 px-4 border-b text-center">{user.email}</td>
                    <td className="py-2 px-4 border-b text-center">{user.mobileNumber}</td>
                    <td className="py-2 px-4 border-b text-center">
                      {user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString('en-GB') : 'Not provided'}
                    </td>
                    <td className="py-2 px-4 border-b text-center">{user.role}</td>
                    <td className="py-2 px-4 border-b text-center">
                      <button 
                        onClick={() => resetPassword(user._id, user.name, user.email)}
                        className="bg-orange-500 text-white px-3 py-1 rounded text-sm hover:bg-orange-600 transition-colors duration-200 flex items-center space-x-1 mx-auto"
                        title="Reset user password and send new credentials via email"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-3.586l6.879-6.88a6 6 0 018.242-.88M13 5l3 3" />
                        </svg>
                        <span>Reset Password</span>
                      </button>
                    </td>
                    <td className="py-2 px-4 border-b text-center">
                      <button onClick={() => openModal(user)} className="text-blue-500 hover:underline mr-2">Edit</button>
                      <button onClick={() => deleteHandler(user._id)} className="text-red-500 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {isModalOpen && <EditUserModal user={selectedUser} closeModal={closeModal} />}
    </div>
  );
};

export default UserManagement;
